import * as React from 'react';
import { createPortal } from 'react-dom';
import { Program } from '~source/core/models/tv-guide';
import { dateToTimeNotation } from '~source/utils/beautify-timing';
import { calculateTimelineItemLayout } from '~source/utils/timeline-layout';
import { getPageX } from '~source/utils/pointer';
import {
    computeTooltipPosition,
    measureTooltipGeometry,
    DEFAULT_TOOLTIP_MARGIN,
    DEFAULT_TOOLTIP_WIDTH,
    TooltipGeometry,
} from '~source/utils/tooltip';
import $ from './timeline-item.scss';
import cx from '~source/utils/join-class-names';
import { isMobileEpgPath } from '~source/utils/is-mobile-epg';
import { navigateToEpgDeepLink } from '~source/utils/epg-deeplink';
import { navigateToEpgWebUrl, buildEpgWebUrl } from '~source/utils/epg-web-url';

export interface TimelineItemProps {
    program: Program;
    isCurrent?: boolean;
}

const TOOLTIP_WIDTH = DEFAULT_TOOLTIP_WIDTH;
const TOOLTIP_MARGIN = DEFAULT_TOOLTIP_MARGIN;
const MIN_LEFT = 10;

export const TimelineItem: React.FC<TimelineItemProps> = ({ program, isCurrent = false }) => {
    const startDate = new Date(program.dateStart);
    const endDate = new Date(program.dateEnd);

    const { left, width } = calculateTimelineItemLayout(startDate, endDate);

    const [visible, setVisible] = React.useState(false);
    const hostRef = React.useRef<HTMLDivElement | null>(null);
    const tooltipRef = React.useRef<HTMLDivElement | null>(null);
    const arrowRef = React.useRef<HTMLSpanElement | null>(null);
    const isTouchDeviceRef = React.useRef<boolean>('ontouchstart' in document.documentElement);

    const rectRef = React.useRef<DOMRect | null>(null);
    const geometryRef = React.useRef<TooltipGeometry>({
        tooltipWidth: TOOLTIP_WIDTH,
        tooltipHeight: 0,
        arrowHalfWidth: 6,
    });

    const rafIdRef = React.useRef<number | null>(null);
    const nextPosRef = React.useRef<{ pageX: number } | null>(null);

    const measureTooltip = () => {
        geometryRef.current = measureTooltipGeometry(
            { tooltip: tooltipRef.current, arrow: arrowRef.current },
            geometryRef.current,
        );
    };

    const tooltipIdRef = React.useRef<string>(`${program.channelId}-${program.dateStart}`);

    const dispatchShowEvent = () => {
        try {
            const ev = new CustomEvent('timeline:tooltip:show', {
                detail: { id: tooltipIdRef.current },
            });
            document.dispatchEvent(ev);
        } catch {
            // ignore
        }
    };

    const updateDOMPosition = (pageX: number) => {
        const tooltip = tooltipRef.current;
        const arrow = arrowRef.current;
        const rect = rectRef.current;
        if (!tooltip || !rect) return;

        const baseGeometry = geometryRef.current;
        const geometry = {
            ...baseGeometry,
            tooltipHeight: baseGeometry.tooltipHeight || tooltip.clientHeight,
        };

        const pos = computeTooltipPosition({
            pageX,
            hostRect: rect,
            geometry,
            isTouchDevice: isTouchDeviceRef.current,
            minLeft: MIN_LEFT,
            margin: TOOLTIP_MARGIN,
        });

        tooltip.style.setProperty('--tooltip-left', `${pos.left}px`);
        tooltip.style.setProperty('--tooltip-top', `${pos.top}px`);

        if (arrow && pos.arrowLeft != null) {
            arrow.style.setProperty('--arrow-left', `${pos.arrowLeft}px`);
        }
    };

    const scheduleFrame = () => {
        if (rafIdRef.current !== null) return;
        rafIdRef.current = window.requestAnimationFrame(() => {
            rafIdRef.current = null;
            const p = nextPosRef.current;
            if (p) updateDOMPosition(p.pageX);
        });
    };

    const onPointerMove = (e: PointerEvent | TouchEvent | MouseEvent) => {
        const pageX = getPageX(e);
        if (pageX == null) return;
        nextPosRef.current = { pageX };
        scheduleFrame();
    };

    // Typed wrappers to satisfy addEventListener signatures without `as any`
    const onPointerMovePointer = (e: PointerEvent) => onPointerMove(e);
    const onPointerMoveTouchStart = (e: TouchEvent) => onPointerMove(e);
    const onPointerMoveTouchMove = (e: TouchEvent) => onPointerMove(e);
    const onPointerMoveMouse = (e: MouseEvent) => onPointerMove(e);

    const addGlobalMove = () => {
        if (isTouchDeviceRef.current) {
            window.addEventListener('touchstart', onPointerMoveTouchStart, { passive: true });
            window.addEventListener('touchmove', onPointerMoveTouchMove, { passive: true });
        } else {
            window.addEventListener('pointermove', onPointerMovePointer, { passive: true });
        }
    };

    const removeGlobalMove = () => {
        if (isTouchDeviceRef.current) {
            window.removeEventListener('touchstart', onPointerMoveTouchStart);
            window.removeEventListener('touchmove', onPointerMoveTouchMove);
        } else {
            window.removeEventListener('pointermove', onPointerMovePointer);
        }
    };

    const hideTooltip = () => {
        setVisible(false);
        removeGlobalMove();
        nextPosRef.current = null;
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
    };

    const handleEnter = (e: React.MouseEvent<HTMLDivElement>) => {
        // In app webview, show tooltip only on long-press
        if (isMobileEpgPath()) return;
        rectRef.current = hostRef.current?.getBoundingClientRect() ?? null;
        dispatchShowEvent();
        setVisible(true);
        requestAnimationFrame(() => {
            measureTooltip();
            addGlobalMove();
            onPointerMoveMouse(e.nativeEvent);
        });
    };

    const handleLeave = () => {
        hideTooltip();
    };

    React.useEffect(
        () => {
            const onAnyTooltipShow = (ev: Event) => {
                const { detail } = ev as CustomEvent<{ id: string }>;
                if (!detail) return;
                const { id } = detail;
                if (id !== tooltipIdRef.current) {
                    hideTooltip();
                }
            };

            const onDocTouchStart = (e: TouchEvent) => {
                const target = (e.target as Node) || null;
                const host = hostRef.current;
                if (host && target && !host.contains(target)) {
                    hideTooltip();
                }
            };

            const onDocMouseDown = (e: MouseEvent) => {
                const target = (e.target as Node) || null;
                const host = hostRef.current;
                if (host && target && !host.contains(target)) {
                    hideTooltip();
                }
            };

            const onWindowScroll = () => {
                hideTooltip();
            };

            document.addEventListener('timeline:tooltip:show', onAnyTooltipShow);
            document.addEventListener('touchstart', onDocTouchStart, { passive: true });
            document.addEventListener('mousedown', onDocMouseDown);
            window.addEventListener('scroll', onWindowScroll, { passive: true });

            return () => {
                document.removeEventListener('timeline:tooltip:show', onAnyTooltipShow);
                document.removeEventListener('touchstart', onDocTouchStart);
                document.removeEventListener('mousedown', onDocMouseDown);
                window.removeEventListener('scroll', onWindowScroll);
                removeGlobalMove();
                if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const inApp = isMobileEpgPath();

    const longPressTimeoutRef = React.useRef<number | null>(null);
    const longPressTriggeredRef = React.useRef(false);

    const clearLongPress = () => {
        if (longPressTimeoutRef.current !== null) {
            window.clearTimeout(longPressTimeoutRef.current);
            longPressTimeoutRef.current = null;
        }
    };

    const onClickItem = (e: React.MouseEvent<HTMLDivElement>) => {
        // Prevent navigation if dragging occurred
        // Check the data attribute on the timeline wrapper (set synchronously on drag end)
        const timelineWrapper = e.currentTarget.closest(
            '[role="region"][aria-label="Tijdlijn"]',
        ) as HTMLElement;
        const wasDragging = timelineWrapper?.getAttribute('data-was-dragging') === 'true';

        if (wasDragging) {
            e.preventDefault();
            e.stopPropagation();
            if (timelineWrapper) {
                timelineWrapper.removeAttribute('data-was-dragging');
            }
            return;
        }

        if (!inApp) {
            const href = buildEpgWebUrl(program);
            if (!href) return;
            navigateToEpgWebUrl(program);
            return;
        }
        if (longPressTriggeredRef.current) {
            e.preventDefault();
            longPressTriggeredRef.current = false;
            return;
        }
        navigateToEpgDeepLink(program);
    };

    const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
        if (!inApp) return;
        longPressTriggeredRef.current = false;
        clearLongPress();
        longPressTimeoutRef.current = window.setTimeout(() => {
            longPressTriggeredRef.current = true;
            rectRef.current = hostRef.current?.getBoundingClientRect() ?? null;
            dispatchShowEvent();
            setVisible(true);
            requestAnimationFrame(() => {
                measureTooltip();
                addGlobalMove();
                if (e.touches && e.touches[0]) {
                    onPointerMoveTouchMove(e.nativeEvent);
                }
            });
        }, 400);
    };

    const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {
        if (!inApp) return;
        clearLongPress();
    };

    const onKeyDownItem: React.KeyboardEventHandler<HTMLDivElement> = React.useCallback(
        (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (inApp) {
                    navigateToEpgDeepLink(program);
                } else {
                    const href = buildEpgWebUrl(program);
                    if (!href) return;
                    navigateToEpgWebUrl(program);
                }
            }
        },
        [inApp, program],
    );

    return (
        <>
            <div
                ref={(el) => {
                    hostRef.current = el;
                }}
                className={cx($.wrap, isCurrent && $.current)}
                style={
                    {
                        width: `${width}px`,
                        left: `${left}px`,
                        '--item-left': `${left}px`,
                        '--item-width': `${width}px`,
                    } as React.CSSProperties
                }
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                onClick={onClickItem}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                role='button'
                tabIndex={0}
                onKeyDown={onKeyDownItem}
            >
                <div className={cx($.row, $.sticky)}>
                    <span className={$.date} style={{ maxWidth: `${width - 12}px` }}>
                        {dateToTimeNotation(startDate)} - {dateToTimeNotation(endDate)}
                    </span>
                </div>
                <div className={cx($.row, $.sticky)}>
                    <h4 className={$.title} style={{ maxWidth: `${width - 12}px` }}>
                        {program.live && <span className={$.live}>LIVE</span>}
                        {program.title}
                    </h4>
                </div>
            </div>

            {visible &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={cx($.tooltip, visible && $['tooltip-visible'])}
                    >
                        <h5 className={$['tooltip-title']}>{program.title}</h5>
                        {program.description && (
                            <p className={$['tooltip-text']}>{program.description}</p>
                        )}
                        <span ref={arrowRef} className={$['tooltip-arrow']} />
                    </div>,
                    document.body,
                )}
        </>
    );
};
